import React, {
	useCallback,
	useRef,
	useState,
	type SyntheticEvent,
	type MouseEvent,
	useMemo,
} from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { CustomThemeButton } from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, {
	type CustomTriggerProps,
	type OnOpenChangeArgs,
	DropdownItem,
} from '@atlaskit/dropdown-menu';
import ShowMoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { token } from '@atlaskit/tokens';
import ComponentWithAnalytics from '@atlassian/jira-analytics-web-react/src/utils/component-with-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ForgeAppsContainer } from '@atlassian/jira-forge-action-apps-controller/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { ModalContextProvider } from '@atlassian/jira-modal-context-provider/src/ModalContextProvider.tsx';
import { ModalEntryPointDropdownItemTrigger } from '@atlassian/jira-modal-entry-point-dropdown-item-trigger/src/ModalEntryPointDropdownItemTrigger.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { SprintActionsConnectMenuItems } from '@atlassian/jira-software-connect-toolbar/src/ui/sprint-connect-action/index.tsx';
import { editSprintModalLoad } from '@atlassian/jira-software-edit-sprint-modal-relay/src/common/utils/performance-analytics/index.tsx';
import { useModalDialogActions } from '@atlassian/jira-software-modal-dialog/src/controllers/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import {
	SPRINT,
	SPRINT_MOVE_TO_TOP,
	SPRINT_MOVE_UP,
	SPRINT_MOVE_DOWN,
	SPRINT_MOVE_TO_BOTTOM,
} from '../../../model/constants.tsx';
import type { CardListType, CardListId } from '../../../model/types.tsx';
import { sprintMoveRequest } from '../../../state/actions/sprints/move/index.tsx';
import { useBacklogDispatch, useBacklogSelector } from '../../../state/index.tsx';
import { getCardLists } from '../../../state/selectors/cards/index.tsx';
import {
	makeIsCardListActiveSprint,
	makeGetSprint,
	makeGetNextSprintId,
	makeGetPreviousNotStartedSprintId,
	getFirstInactiveSprintId,
	getLastInactiveSprintId,
} from '../../../state/selectors/sprints/index.tsx';
import { useIsCMPBacklog } from '../../../state/state-hooks/capabilities.tsx';
import EditSprintModalRelay from '../../modals/edit-sprint-modal-relay/index.tsx';
import { sprintDeleteModalEntryPoint } from '../../modals/sprint-delete-modal/entrypoint.tsx';
import { darkerButtonTheme, getSprintState } from '../utils.tsx';
import { ForgeSprintActionMenuItems } from './forge-items/index.tsx';
import { SprintActionForgeRuntimeContainer } from './forge-runtime-container/index.tsx';
import messages from './messages.tsx';

const DropdownItemWithAnalytics = ComponentWithAnalytics('dropdownItem', {
	onClick: 'clicked',
})(DropdownItem);

export default function Menu({
	cardListId,
	cardListName,
	cardListType,
}: {
	cardListId: CardListId;
	cardListName: string;
	cardListType: CardListType;
}) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isCMPBoard = useIsCMPBacklog();
	const { formatMessage } = useIntl();
	const { setReturnFocusTo } = useModalDialogActions();
	const element = useRef<HTMLElement | null>(null);
	const [isEditSprintModalOpen, setIsEditSprintModalOpen] = useState(false);
	const sprint = useBacklogSelector((state) => makeGetSprint(state)(cardListId));
	const previousSprintId = useBacklogSelector((state) =>
		makeGetPreviousNotStartedSprintId(state)(cardListId),
	);
	const nextSprintId = useBacklogSelector((state) => makeGetNextSprintId(state)(cardListId));

	const firstInactiveSprintId = useBacklogSelector(getFirstInactiveSprintId);
	const lastInactiveSprintId = useBacklogSelector(getLastInactiveSprintId);

	const isCardListActiveSprint = useBacklogSelector((state) =>
		makeIsCardListActiveSprint(state)(cardListId),
	);

	const sprintCardList = useBacklogSelector((state) =>
		cardListId ? getCardLists(state)[String(cardListId)] : null,
	);

	const canMoveSprintUp = previousSprintId !== null;
	const canMoveSprintDown = !isCardListActiveSprint && nextSprintId !== null;

	const enabledMenuActions: { [key: string]: boolean } =
		cardListType !== SPRINT
			? {}
			: {
					MOVE_SPRINT_TO_TOP: canMoveSprintUp,
					MOVE_SPRINT_UP: canMoveSprintUp,
					MOVE_SPRINT_DOWN: canMoveSprintDown,
					MOVE_SPRINT_TO_BOTTOM: canMoveSprintDown,
					EDIT_SPRINT: true,
					DELETE_SPRINT: true,
				};

	const dispatch = useBacklogDispatch();

	const renderTrigger = useCallback(
		({ triggerRef, onClick, ...props }: CustomTriggerProps) => {
			const handleRef = (refElement: HTMLElement | null) => {
				if (typeof triggerRef === 'function') {
					triggerRef(refElement);
				}
				if (refElement) {
					element.current = refElement;
				}
			};

			const handleClick = (event: MouseEvent) => {
				const analyticsEvent = createAnalyticsEvent({
					actionSubject: 'dropdownMenu',
					action: 'toggled',
				});
				fireUIAnalytics(analyticsEvent, 'sprintsMenu');
				if (onClick) {
					onClick(event);
				}
			};

			// TODO: remove this icon on visual-refresh cleanup
			const moreIcon = (
				<ShowMoreHorizontalIcon
					label="More actions"
					color={token('color.icon')}
					spacing="spacious"
					LEGACY_fallbackIcon={MoreIcon}
					LEGACY_primaryColor={token('color.icon')}
				/>
			);
			return isVisualRefreshEnabled() ? (
				<IconButton
					{...props}
					icon={ShowMoreIcon}
					label={formatMessage(messages.menuTrigger, {
						sprintName: cardListName,
					})}
					ref={handleRef}
					appearance="subtle"
					spacing="compact"
					testId={`software-backlog.card-list.sprints-menu.${cardListName}`}
					onClick={handleClick}
					{...(fg('visual-refresh-meatball-menu-button-tooltips')
						? {
								isTooltipDisabled: false,
								tooltip: {
									content: formatMessage(messages.moreActionsTooltip),
									hideTooltipOnClick: true,
								},
							}
						: {})}
				/>
			) : (
				<CustomThemeButton
					{...props}
					ref={handleRef}
					iconBefore={moreIcon}
					aria-label={formatMessage(messages.menuTrigger, {
						sprintName: cardListName,
					})}
					theme={darkerButtonTheme}
					testId={`software-backlog.card-list.sprints-menu.${cardListName}`}
					onClick={handleClick}
				/>
			);
		},
		[cardListName, formatMessage, createAnalyticsEvent],
	);

	const [isOpen, setIsOpen] = useState(false);

	const handleOpenChange = useCallback(
		(attrs: OnOpenChangeArgs) => {
			setReturnFocusTo(element);
			setIsOpen(attrs.isOpen);
		},
		[element, setReturnFocusTo, setIsOpen],
	);

	// handle close when not initiated by user interaction. e.g. when forge apps finish loading
	const handleNonClickClose = useCallback(() => {
		setIsOpen(false);
	}, [setIsOpen]);

	const entryPointProps = useMemo(
		() => ({
			sprintId: cardListId,
			onClose: () => {
				const analyticsEvent = createAnalyticsEvent({
					actionSubject: 'button',
					action: 'clicked',
				});
				fireUIAnalytics(analyticsEvent, 'cancelButton');
			},
		}),
		[cardListId, createAnalyticsEvent],
	);

	const isVisible = enabledMenuActions && Object.keys(enabledMenuActions).length > 0;
	if (!isVisible) {
		return null;
	}

	const onMoveUp = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		if (previousSprintId != null && previousSprintId !== '') {
			dispatch(
				sprintMoveRequest({
					sprintId: cardListId,
					targetSprintId: previousSprintId,
					direction: SPRINT_MOVE_UP,
					analyticsEvent,
				}),
			);
		}

		fireUIAnalytics(analyticsEvent, 'moveSprintUp');
	};

	const onMoveToTop = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		dispatch(
			sprintMoveRequest({
				sprintId: cardListId,
				targetSprintId: firstInactiveSprintId,
				direction: SPRINT_MOVE_TO_TOP,
				analyticsEvent,
			}),
		);

		fireUIAnalytics(analyticsEvent, 'moveSprintToTop');
	};

	const onMoveDown = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		if (nextSprintId != null && nextSprintId !== '') {
			dispatch(
				sprintMoveRequest({
					sprintId: cardListId,
					targetSprintId: nextSprintId,
					direction: SPRINT_MOVE_DOWN,
					analyticsEvent,
				}),
			);
		}

		fireUIAnalytics(analyticsEvent, 'moveSprintDown');
	};

	const onMoveToBottom = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		dispatch(
			sprintMoveRequest({
				sprintId: cardListId,
				targetSprintId: lastInactiveSprintId,
				direction: SPRINT_MOVE_TO_BOTTOM,
				analyticsEvent,
			}),
		);

		fireUIAnalytics(analyticsEvent, 'moveSprintToBottom');
	};

	const onEditSprintClick = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		setIsEditSprintModalOpen(true);
		fireUIAnalytics(analyticsEvent, 'openEditSprint');
		editSprintModalLoad.start();
	};

	const onEditSprintModalCloseClick = () => {
		setIsEditSprintModalOpen(false);
	};

	const menuItems = (
		<>
			{enabledMenuActions.MOVE_SPRINT_TO_TOP && (
				<DropdownItemWithAnalytics
					data-testid="software-backlog.card-list.sprints-menu.sprint-move-to-top"
					onClick={onMoveToTop}
				>
					{formatMessage(messages.moveSprintToTop)}
				</DropdownItemWithAnalytics>
			)}
			{enabledMenuActions.MOVE_SPRINT_UP && (
				<DropdownItemWithAnalytics
					data-testid="software-backlog.card-list.sprints-menu.sprint-move-up"
					onClick={onMoveUp}
				>
					{formatMessage(messages.moveSprintUp)}
				</DropdownItemWithAnalytics>
			)}
			{enabledMenuActions.MOVE_SPRINT_DOWN && (
				<DropdownItemWithAnalytics
					data-testid="software-backlog.card-list.sprints-menu.sprint-move-down"
					onClick={onMoveDown}
				>
					{formatMessage(messages.moveSprintDown)}
				</DropdownItemWithAnalytics>
			)}
			{enabledMenuActions.MOVE_SPRINT_TO_BOTTOM && (
				<DropdownItemWithAnalytics
					data-testid="software-backlog.card-list.sprints-menu.sprint-move-to-bottom"
					onClick={onMoveToBottom}
				>
					{formatMessage(messages.moveSprintToBottom)}
				</DropdownItemWithAnalytics>
			)}
			{enabledMenuActions.EDIT_SPRINT && (
				<DropdownItemWithAnalytics
					data-testid="software-backlog.card-list.sprints-menu.sprint-edit"
					onClick={onEditSprintClick}
				>
					{formatMessage(messages.editSprint)}
				</DropdownItemWithAnalytics>
			)}

			{enabledMenuActions.DELETE_SPRINT && sprintCardList && (
				<ModalEntryPointDropdownItemTrigger
					entryPoint={sprintDeleteModalEntryPoint}
					entryPointProps={entryPointProps}
					interactionName="sprint-delete-modal"
					testId="software-backlog.card-list.sprints-menu.sprint-delete"
					useInternalModal={false}
				>
					{formatMessage(messages.deleteSprint)}
				</ModalEntryPointDropdownItemTrigger>
			)}
			{isCMPBoard && (
				<>
					<SprintActionsConnectMenuItems sprintId={cardListId} location="sprint-move-actions" />
					<SprintActionsConnectMenuItems sprintId={cardListId} location="sprint-edit-action" />
					<SprintActionsConnectMenuItems sprintId={cardListId} location="sprint-delete-action" />
				</>
			)}
			{fg('forge-ui-project-web-items') && (
				<ForgeSprintActionMenuItems
					sprintId={cardListId}
					sprintState={getSprintState(sprint?.state)}
				/>
			)}
		</>
	);

	if (fg('forge-ui-project-web-items')) {
		return (
			<ForgeAppsContainer scope={`sprint-action-${cardListId}`}>
				<ModalContextProvider>
					{isEditSprintModalOpen && (
						<EditSprintModalRelay
							cardListId={Number(cardListId)}
							onClose={onEditSprintModalCloseClick}
							onSubmit={onEditSprintModalCloseClick}
						/>
					)}
					<DropdownMenu
						isOpen={isOpen}
						onOpenChange={handleOpenChange}
						trigger={renderTrigger}
						placement="bottom-end"
						shouldRenderToParent
						testId="software-backlog.card-list.sprints-menu.dropdown-menu"
					>
						{menuItems}
					</DropdownMenu>
					<SprintActionForgeRuntimeContainer onAppsFinishLoading={handleNonClickClose} />
				</ModalContextProvider>
			</ForgeAppsContainer>
		);
	}

	return (
		<>
			<ModalContextProvider>
				{isEditSprintModalOpen && (
					<EditSprintModalRelay
						cardListId={Number(cardListId)}
						onClose={onEditSprintModalCloseClick}
						onSubmit={onEditSprintModalCloseClick}
					/>
				)}
				<DropdownMenu
					onOpenChange={handleOpenChange}
					trigger={renderTrigger}
					placement="bottom-end"
					shouldRenderToParent
					testId="software-backlog.card-list.sprints-menu.dropdown-menu"
				>
					{menuItems}
				</DropdownMenu>
			</ModalContextProvider>
		</>
	);
}
